'use client'
import React, {createContext, useContext, useState, useEffect, useRef} from 'react';
import {signOut, useSession} from "next-auth/react";
import {getLocalizedUrl} from '@/utils/i18n';
import {useParams, useRouter} from "next/navigation";
import type {Locale} from '@/configs/i18n'
import {checkPermissions} from "@/utils/roles";


// Creazione del contesto per la sessione
const SessionContext = createContext<any>(null);

// Hook per accedere al contesto
export const useSessionContext = () => useContext(SessionContext);


let refreshCond = 0

// Componente Provider per la sessione
export const SessionProvider = ({children}: any) => {
    const [session, setSession]: any = useState(null);
    const mySession: any = useSession({
        required: true,
        // refetchOnWindowFocus va sul next-auth SessionProvider (non su useSession).
        onUnauthenticated() {
            // Questa callback viene chiamata quando la sessione non è più valida
            logOutLocal()
        },
    });
    const {lang: locale}: any = useParams()
    const router = useRouter()

    const [refresh, setRefresh] = useState(false)
    // Debounce /auth redirect: evita redirect prematuro durante hydration sessione
    const authRedirectTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

    // Funzione locale per il logout che può essere chiamata dalla callback
    const logOutLocal = () => {
        localStorage.clear()
        router.push(getLocalizedUrl('/login', locale as Locale))
    }


    const RefreshComponent = (number: number): any => {
        refreshCond = number
        setRefresh(!refresh)

        return 'Refresh Ok!'
    }

    const waitPermission = async (mySession: any) => {
        return await checkPermissions(mySession.user.permissions, mySession.user.role)
    }


    useEffect(() => {
        // Cancella sempre il timer pendente all'inizio: se tokenApi arriva
        // nel render successivo il redirect viene annullato automaticamente.
        if (authRedirectTimer.current) {
            clearTimeout(authRedirectTimer.current)
            authRedirectTimer.current = null
        }

        if (mySession.status === 'authenticated') {
            // Controlla se la sessione è stata marcata per il logout (token scaduto)
            if (mySession?.data?.logout === true) {
                logOut()
                return
            }

            // Stato autenticato ma data ancora non popolato: aspetta il prossimo tick
            // invece di forzare logout (evita loop su refetch transitorio).
            if (!mySession?.data) {
                return
            }

            if (!mySession?.data?.tokenApi) {
                // Debounce: aspetta 800ms prima di agire.
                // Copre la race condition in cui tokenApi non è ancora presente
                // durante la prima hydration della sessione su refresh.
                // Se tokenApi arriva prima degli 800ms il timer viene annullato sopra.
                //
                // Se dopo il debounce tokenApi è ancora assente, la sessione è
                // irrecuperabile: il cookie httpOnly api-token è scaduto/mancante
                // mentre il session-token NextAuth è ancora valido (le due scadenze
                // possono divergere). NON reindirizzare a /auth: AuthIntermezzo è
                // gated dietro tokenApi qui sotto e non monterebbe mai → loop infinito
                // su /auth risolvibile solo cancellando i cookie. Forziamo invece il
                // logout (self-heal) che riporta pulito al login.
                authRedirectTimer.current = setTimeout(() => {
                    logOut()
                }, 800)
                return
            }

            waitPermission(mySession.data).then((r: any) => {
                let data = mySession.data
                data.permissions = r.permessi_roles
                setSession((prev: any) => ({
                    ...prev,
                    session: data
                }))
            })
        } else if (mySession.status === 'unauthenticated') {
            // Se la sessione non è autenticata, reindirizza al login
            logOut()
        }
    }, [mySession.status, mySession?.data]);

    async function logOut() {
        localStorage.clear()
        await signOut({redirect: false})

        router.push(getLocalizedUrl('/login', locale as Locale))
    }

    // Mostra un loader durante il loading iniziale O mentre si caricano i permessi
    if (mySession.status === 'loading' || (mySession.status === 'authenticated' && !session?.session)) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                width: '100vw'
            }}>
                <div style={{ textAlign: 'center' }}>
                    <div style={{
                        border: '4px solid #f3f3f3',
                        borderTop: '4px solid #3498db',
                        borderRadius: '50%',
                        width: '40px',
                        height: '40px',
                        animation: 'spin 1s linear infinite',
                        margin: '0 auto'
                    }} />
                    <style>{`
                        @keyframes spin {
                            0% { transform: rotate(0deg); }
                            100% { transform: rotate(360deg); }
                        }
                    `}</style>
                </div>
            </div>
        )
    }

    // Se la sessione è autenticata e abbiamo i dati, mostra i children
    if (mySession.status === 'authenticated' && session?.session) {
        return (
            <SessionContext.Provider value={{sessionContext: session, RefreshComponent, refreshCond}}>
                {children}
            </SessionContext.Provider>
        )
    }

    // Per tutti gli altri casi (unauthenticated, errore), mostra il loader
    // mentre l'useEffect si occupa del redirect per evitare flash di pagina bianca
    return (
        <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            width: '100vw'
        }}>
            <div style={{ textAlign: 'center' }}>
                <div style={{
                    border: '4px solid #f3f3f3',
                    borderTop: '4px solid #3498db',
                    borderRadius: '50%',
                    width: '40px',
                    height: '40px',
                    animation: 'spin 1s linear infinite',
                    margin: '0 auto'
                }} />
                <style>{`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}</style>
            </div>
        </div>
    )
};